import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Modal,
  TextInput,
  Textarea,
  Select,
  ActionIcon,
  Accordion,
  Badge,
  LoadingOverlay
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { IconPlus, IconEdit, IconTrash, IconBuildingStore, IconBuilding } from '@tabler/icons-react';
import { pb, fetchDirektionen, fetchDienststellen } from '../../lib/pocketbase';
import { notifications } from '@mantine/notifications';
import { BereichAbteilung } from '../Dashboard/types';

interface BereichFormData {
  name: string;
  dienststelle: string;
  adresse: string;
  bemerkungen: string;
}

interface Direktion {
  id: string;
  name: string;
}

interface Dienststelle {
  id: string;
  name: string;
  direktion: string;
}

export function BereichVerwaltung() {
  const [bereiche, setBereiche] = useState<BereichAbteilung[]>([]);
  const [direktionen, setDirektionen] = useState<{value: string, label: string}[]>([]);
  const [dienststellen, setDienststellen] = useState<Dienststelle[]>([]);
  const [loading, setLoading] = useState(true);
  const [opened, { open, close }] = useDisclosure(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const form = useForm<BereichFormData>({
    initialValues: {
      name: '',
      dienststelle: '',
      adresse: '',
      bemerkungen: '',
    },
    validate: {
      name: (value) => (value.length < 1 ? 'Name ist erforderlich' : null),
      dienststelle: (value) => (!value ? 'Bitte wählen Sie eine Dienststelle' : null),
    },
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [bereicheData, direktionenData] = await Promise.all([
        pb.collection('bereich_abteilung').getFullList<BereichAbteilung>({
          sort: 'name',
          expand: 'dienststelle',
        }),
        fetchDirektionen(),
      ]);
      
      // Lade alle Dienststellen mit expand
      const dienststellenData = await pb.collection('dienststellen').getFullList<Dienststelle>({
        sort: 'name',
        expand: 'direktion',
      });
      
      setBereiche(bereicheData);
      setDirektionen(direktionenData);
      setDienststellen(dienststellenData);
    } catch (error) {
      console.error('Error loading data:', error);
      notifications.show({
        title: 'Fehler',
        message: 'Daten konnten nicht geladen werden',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (values: BereichFormData) => {
    try {
      if (editingId) {
        await pb.collection('bereich_abteilung').update(editingId, values);
        notifications.show({
          title: 'Erfolg',
          message: 'Bereich wurde aktualisiert',
          color: 'green',
        });
      } else {
        await pb.collection('bereich_abteilung').create(values);
        notifications.show({
          title: 'Erfolg',
          message: 'Bereich wurde erstellt',
          color: 'green',
        });
      }
      close();
      loadData();
      form.reset();
      setEditingId(null);
    } catch (error) {
      console.error('Error saving:', error);
      notifications.show({
        title: 'Fehler',
        message: 'Speichern fehlgeschlagen',
        color: 'red',
      });
    }
  };

  const handleEdit = (bereich: BereichAbteilung) => {
    setEditingId(bereich.id);
    form.setValues({
      name: bereich.name,
      dienststelle: bereich.dienststelle,
      adresse: bereich.adresse || '',
      bemerkungen: bereich.bemerkungen || '',
    });
    open();
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Möchten Sie den Bereich "${name}" wirklich löschen?`)) return;

    try {
      await pb.collection('bereich_abteilung').delete(id);
      notifications.show({
        title: 'Erfolg',
        message: 'Bereich wurde gelöscht',
        color: 'green',
      });
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      notifications.show({
        title: 'Fehler',
        message: 'Löschen fehlgeschlagen',
        color: 'red',
      });
    }
  };

  const openNew = () => {
    setEditingId(null);
    form.reset();
    open();
  };

  // Gruppiere Dienststellen nach Direktion
  const dienststellenByDirektion = direktionen.reduce((acc, dir) => {
    acc[dir.value] = dienststellen.filter(ds => ds.direktion === dir.value);
    return acc;
  }, {} as Record<string, Dienststelle[]>);

  // Gruppiere Bereiche nach Dienststelle
  const bereicheByDienststelle = dienststellen.reduce((acc, ds) => {
    acc[ds.id] = bereiche.filter(b => b.dienststelle === ds.id);
    return acc;
  }, {} as Record<string, BereichAbteilung[]>);

  // Hole Dienststellen-Optionen für Dropdown
  const dienststellenOptions = dienststellen.map(ds => ({
    value: ds.id,
    label: ds.name
  }));

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Bereiche & Abteilungen</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openNew}>
          Neuer Bereich
        </Button>
      </Group>

      <Paper shadow="sm" p="md" withBorder pos="relative">
        <LoadingOverlay visible={loading} />
        
        <Accordion variant="separated" multiple>
          {direktionen.map((dir) => {
            const dsList = dienststellenByDirektion[dir.value] || [];
            const totalBereiche = dsList.reduce((sum, ds) => sum + (bereicheByDienststelle[ds.id]?.length || 0), 0);
            
            return (
              <Accordion.Item key={dir.value} value={dir.value}>
                <Accordion.Control icon={<IconBuilding size={20} />}>
                  <Group justify="space-between" pr="md">
                    <Text fw={600}>{dir.label}</Text>
                    <Group gap="xs">
                      <Badge variant="light" color="blue">{dsList.length} Dienststellen</Badge>
                      <Badge variant="light" color="gray">{totalBereiche} Bereiche</Badge>
                    </Group>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {dsList.length === 0 ? (
                    <Text c="dimmed" fs="italic" size="sm">Keine Dienststellen vorhanden</Text>
                  ) : (
                    <Accordion variant="contained" multiple>
                      {dsList.map((ds) => {
                        const items = bereicheByDienststelle[ds.id] || [];
                        return (
                          <Accordion.Item key={ds.id} value={ds.id}>
                            <Accordion.Control icon={<IconBuildingStore size={18} />}>
                              <Group justify="space-between" pr="md">
                                <Text fw={500}>{ds.name}</Text>
                                <Badge variant="light" color="gray" size="sm">{items.length} Bereiche</Badge>
                              </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                              {items.length === 0 ? (
                                <Text c="dimmed" fs="italic" size="sm">Keine Bereiche vorhanden</Text>
                              ) : (
                                <Stack gap="xs">
                                  {items.map((item) => (
                                    <Paper key={item.id} withBorder p="sm" bg="gray.0">
                                      <Group justify="space-between">
                                        <Stack gap={2}>
                                          <Text fw={500} size="sm">
                                            {item.name === '-' ? '(Hauptstandort)' : item.name}
                                          </Text>
                                          {item.adresse && (
                                            <Text size="xs" c="dimmed" style={{ whiteSpace: "pre-line" }}>
                                              {item.adresse?.replaceAll("\\n", "\n")}
                                            </Text>
                                          )}
                                          {item.bemerkungen && (
                                            <Text size="xs" c="dimmed" fs="italic">{item.bemerkungen}</Text>
                                          )}
                                        </Stack>
                                        <Group gap={4}>
                                          <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(item)}>
                                            <IconEdit size={16} />
                                          </ActionIcon>
                                          <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(item.id, item.name)}>
                                            <IconTrash size={16} />
                                          </ActionIcon>
                                        </Group>
                                      </Group>
                                    </Paper>
                                  ))}
                                </Stack>
                              )}
                              <Button 
                                variant="light" 
                                size="xs" 
                                mt="md" 
                                leftSection={<IconPlus size={14} />}
                                onClick={() => {
                                    form.setValues({
                                        name: '',
                                        dienststelle: ds.id,
                                        adresse: '',
                                        bemerkungen: ''
                                    });
                                    setEditingId(null);
                                    open();
                                }}
                              >
                                Bereich hinzufügen
                              </Button>
                            </Accordion.Panel>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Paper>

      <Modal opened={opened} onClose={close} title={editingId ? "Bereich bearbeiten" : "Neuer Bereich"}>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <Select
              label="Dienststelle"
              placeholder="Wählen Sie eine Dienststelle"
              data={dienststellenOptions}
              searchable
              {...form.getInputProps('dienststelle')}
              required
            />
            <TextInput
              label="Name"
              placeholder="Bereichsbezeichnung (oder '-' für Hauptstandort)"
              {...form.getInputProps('name')}
              required
            />
            <Textarea
              label="Adresse"
              placeholder="Adresse des Bereichs"
              autosize
              minRows={2}
              {...form.getInputProps('adresse')}
              value={form.values.adresse?.replaceAll("\\n", "\n")}
              onChange={(event) =>
                form.setFieldValue("adresse", event.currentTarget.value.replaceAll("\n", "\\n"))
              }
            />
            <Textarea
              label="Bemerkungen"
              placeholder="Zusätzliche Bemerkungen"
              autosize
              minRows={2}
              {...form.getInputProps('bemerkungen')}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>Abbrechen</Button>
              <Button type="submit">Speichern</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
}
